import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * A35 (SPEC-038 Wave 3 FE-1): safe UUID generator with fallback.
 *
 * `crypto.randomUUID()` is unavailable in:
 *   - Older Safari (< 15.4, 2022 release)
 *   - Non-HTTPS contexts (http://localhost is exempt, but plain http on a
 *     LAN IP is not — affects dev previews on mobile)
 *   - Old webviews / Cordova containers
 *
 * Previously, calling `crypto.randomUUID()` directly threw a TypeError in
 * those environments. The wallet withdrawal flow (clientRequestId for
 * idempotency) is the main caller — a throw there blocked ALL withdrawals
 * on those browsers.
 *
 * Use `safeUUID()` everywhere a v4 UUID is needed. Falls back to a
 * spec-compliant Math.random-based generator when crypto.randomUUID is
 * absent (RFC 4122 §4.4: version nibble + variant bits).
 */
export function safeUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * P-UX-013 (SPEC-165): standardized avatar letter fallback.
 *
 * The QA UX Specialist reported 3 distinct avatar states across different
 * pages/reloads ("q" → "QU" → "U"). The previous logic used per-word
 * initials (e.g. "John Doe" → "JD") with a fallback to "U" when
 * displayName was empty. This produced inconsistent letter counts (1 vs
 * 2 chars) depending on whether displayName had a space, and a misleading
 * "U" placeholder during the brief pre-redirect window when `user` was
 * null (race condition tied to deuda técnica P-282 — useAuth refetching
 * /auth/me ~190 times per session).
 *
 * Standardized logic per SPEC-165 P-UX-013:
 *   1. If `displayName` is non-empty: take the first 2 letters,
 *      uppercased. ("John Doe" → "JO", "Anderson" → "AN")
 *   2. Else if `username` is non-empty: take the first 2 letters,
 *      uppercased. (fallback chain per spec).
 *   3. Else: "?" placeholder (NOT "U" — "U" looked like a real initial
 *      and contributed to the "U Networker" corrupt state — a question
 *      mark signals "unknown user" clearly).
 *
 * Pure UI helper — no business logic.
 *
 * @example
 * getAvatarInitials('John Doe', 'jdoe')    // 'JO'
 * getAvatarInitials('Anderson', 'andrey')  // 'AN'
 * getAvatarInitials('', 'jdoe')            // 'JD'
 * getAvatarInitials('', '')                // '?'
 * getAvatarInitials('Q', '')               // 'Q'
 */
export function getAvatarInitials(displayName: string, username: string): string {
  const fromName = (displayName ?? '').trim().slice(0, 2).toUpperCase();
  if (fromName.length > 0) return fromName;
  const fromUsername = (username ?? '').trim().slice(0, 2).toUpperCase();
  if (fromUsername.length > 0) return fromUsername;
  return '?';
}
